import { Link } from "react-router-dom";
import {
  MapPin, ShieldCheck, Zap, FileText, SlidersHorizontal, Clock,
  CheckCircle2, ArrowRight, Ruler, BadgeDollarSign, Building2,
} from "lucide-react";
import Seo from "@/components/Seo";
import RoofDiagram from "@/components/RoofDiagram";
import { useAuth } from "@/context/AuthContext";

const FAQS = [
  { q: "How accurate are the roof measurements?", a: "Reports are generated from high-resolution aerial imagery and include total squares, pitch breakdown, and precise edge lengths (ridges, hips, valleys, eaves, and rakes) — accurate enough to build material orders and insurance estimates without climbing the roof." },
  { q: "How much does a roof report cost?", a: "You pay per report — no subscription and no enterprise contract. The exact price is shown before you pay, typically around $20–$30 per residential property." },
  { q: "How long does a report take?", a: "Ordering takes seconds. We then generate your measurement report — usually within minutes, though some properties can take a few hours during peak times. You don't have to wait on the page: we email you the moment it's ready and save it to your account." },
  { q: "What if there's no imagery for an address?", a: "We check coverage before you pay. If imagery isn't available for a property, we tell you up front and never charge you for a report that can't be fulfilled." },
  { q: "Can I use these reports for insurance claims?", a: "Yes. Contractors use MeasureTheRoof reports to support estimates and insurance claim documentation, including a downloadable PDF for each job." },
  { q: "Do you cover the whole United States?", a: "MeasureTheRoof serves residential and commercial properties across the United States." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const HOWTO_STEPS = [
  { icon: MapPin, t: "Enter the address", d: "Search and confirm the property on a satellite map." },
  { icon: ShieldCheck, t: "Check coverage & price", d: "We confirm imagery exists and show the exact price." },
  { icon: BadgeDollarSign, t: "Pay & order", d: "Securely pay per report with Stripe — one click." },
  { icon: FileText, t: "Get your report", d: "Measurements + PDF delivered, saved to your account." },
];

const howToJsonLd = {
  "@type": "HowTo",
  name: "How to get an aerial roof measurement report",
  description: "Order a contractor-ready roof measurement report for any US property in four steps — no ladder required.",
  totalTime: "PT5M",
  estimatedCost: { "@type": "MonetaryAmount", currency: "USD", value: "22.50" },
  step: HOWTO_STEPS.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.t,
    text: s.d,
    url: "https://www.measuretheroof.com/#how-it-works",
  })),
};

// Combined homepage structured data (AEO/GEO): FAQ + HowTo in one @graph.
const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [faqJsonLd, howToJsonLd],
};

export default function Landing() {
  const { user } = useAuth();
  const authed = user && typeof user === "object";
  const startHref = authed ? "/dashboard" : "/signup";

  return (
    <div data-testid="landing-page">
      <Seo
        title="MeasureTheRoof — Aerial Roof Measurement Reports for Contractors"
        description="Get accurate aerial roof measurement reports for any US property in minutes. Total squares, pitch breakdown, and edge lengths for fast estimates and insurance claims. Pay per report — no contract."
        path="/"
        jsonLd={homeJsonLd}
      />

      {/* Hero */}
      <section className="bg-navy text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "28px 28px" }} aria-hidden="true" />
        <div className="container-mtr relative py-16 sm:py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="eyebrow">For US roofing contractors</span>
            <h1 className="font-display font-black text-[40px] leading-[1.02] sm:text-[52px] lg:text-[64px] mt-3 uppercase">
              Order any roof report <span className="text-orange">in seconds</span>
            </h1>
            <p className="text-[17px] text-white/80 mt-5 max-w-xl leading-relaxed">
              Skip the ladder and the guesswork. Enter an address, confirm coverage, and order a
              contractor-ready report with total squares, pitch breakdown, and edge lengths. We deliver it — usually in minutes, sometimes a few hours — straight to your inbox while you keep working.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to={startHref} className="btn-primary !min-h-[52px] !px-7 text-[16px]" data-testid="hero-cta">
                Get your first report <ArrowRight size={18} />
              </Link>
              <Link to="/sample-report" className="btn-ghost !min-h-[52px] !px-7 !text-white !border-white/40 hover:!bg-white hover:!text-navy text-[16px]" data-testid="hero-sample">
                View a sample report
              </Link>
            </div>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-[14px] text-white/75">
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-orange" /> No contract</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-orange" /> Pay per report</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-orange" /> Emailed when ready</li>
            </ul>
          </div>

          <div className="animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="bg-white rounded-card shadow-modal p-5 sm:p-6 text-iron">
              <div className="flex items-center justify-between mb-4">
                <span className="eyebrow">Sample report</span>
                <span className="badge-complete"><span className="w-1.5 h-1.5 rounded-full bg-current" /> Complete</span>
              </div>
              <RoofDiagram className="w-full h-40 rounded-input mb-4" />
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Total squares" value="30.6" />
                <Stat label="Predominant pitch" value="6/12" />
                <Stat label="Roof facets" value="10" />
                <Stat label="Ridges" value="78 ft" />
                <Stat label="Valleys" value="32 ft" />
                <Stat label="Eaves" value="96 ft" />
              </div>
              <Link to="/sample-report" className="text-orange font-semibold text-[14px] mt-4 inline-flex items-center gap-1" data-testid="hero-sample-link">
                See the full sample report <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="container-mtr py-16 sm:py-20">
        <div className="max-w-2xl">
          <span className="eyebrow">Why contractors switch</span>
          <h2 className="font-body font-extrabold text-navy text-[28px] sm:text-[32px] mt-2">Everything you need to bid with confidence</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          <Feature icon={Zap} title="Order in seconds" desc="Place an order from your truck in seconds. Your report is generated — typically minutes, sometimes a few hours — and we email you the second it's ready." />
          <Feature icon={ShieldCheck} title="Coverage checked first" desc="We verify aerial imagery exists before you pay — so you never burn money on an unfulfillable order." />
          <Feature icon={Ruler} title="Contractor-ready data" desc="Total squares, pitch breakdown, and every edge length you need to order materials accurately." />
          <Feature icon={SlidersHorizontal} title="Built-in waste factor" desc="Adjust waste from 5–25% and instantly see the squares to actually order — no spreadsheet math." />
          <Feature icon={FileText} title="Downloadable PDF" desc="Attach a clean report to any job file or insurance claim, and keep a full history of every order." />
          <Feature icon={BadgeDollarSign} title="Transparent pricing" desc="See the exact price before you confirm. Pay per report, cancel anytime, no enterprise contract." />
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="bg-white border-y border-gravel py-16 sm:py-20">
        <div className="container-mtr">
          <div className="max-w-2xl">
            <span className="eyebrow">How it works</span>
            <h2 className="font-body font-extrabold text-navy text-[28px] sm:text-[32px] mt-2">From address to estimate in four steps</h2>
          </div>
          <ol className="grid md:grid-cols-4 gap-6 mt-10">
            {HOWTO_STEPS.map((s, i) => (
              <li key={i} className="relative">
                <div className="w-11 h-11 rounded-full bg-navy text-white grid place-items-center font-body font-extrabold mb-4">{i + 1}</div>
                <s.icon className="text-orange mb-2" size={24} aria-hidden="true" />
                <h3 className="font-bold text-navy text-[17px]">{s.t}</h3>
                <p className="text-[14px] text-mortar mt-1">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="container-mtr py-16 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="eyebrow">Simple pricing</span>
            <h2 className="font-body font-extrabold text-navy text-[28px] sm:text-[32px] mt-2">Pay per report. No surprises.</h2>
            <p className="text-[16px] text-iron mt-4 max-w-lg leading-relaxed">
              No monthly fees, no minimums, no enterprise paperwork. You see the exact price for each
              property before you pay, and only get charged when you place the order.
            </p>
            <ul className="space-y-3 mt-6">
              {["Coverage verified before payment", "Exact price shown up front", "Email when your report is ready", "Full report history saved to your account"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[15px] text-iron">
                  <CheckCircle2 className="text-go-green shrink-0" size={20} /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="card-mtr border-2 border-navy">
            <span className="badge-pending"><Clock size={14} /> Most popular</span>
            <h3 className="font-body font-extrabold text-navy text-[22px] mt-4">Premium Residential Report</h3>
            <p className="text-mortar text-[14px] mt-1">Complete measurements + PDF for a single property.</p>
            <div className="flex items-end gap-2 mt-5">
              <span className="metric text-[44px] leading-none">$22.50</span>
              <span className="text-mortar text-[14px] mb-1">avg / report</span>
            </div>
            <Link to={startHref} className="btn-primary w-full mt-6" data-testid="pricing-cta">Start now <ArrowRight size={18} /></Link>
            <p className="text-[12px] text-mortar mt-3 text-center">Exact price always shown before you pay.</p>
          </div>
        </div>
      </section>

      {/* FAQ (AEO) */}
      <section className="bg-white border-y border-gravel py-16 sm:py-20">
        <div className="container-mtr max-w-3xl">
          <span className="eyebrow">FAQ</span>
          <h2 className="font-body font-extrabold text-navy text-[28px] sm:text-[32px] mt-2 mb-8">Answers for roofing pros</h2>
          <div className="divide-y divide-gravel">
            {FAQS.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="font-bold text-navy text-[17px] pr-6">{f.q}</h3>
                  <span className="text-orange text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-[15px] text-iron mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-navy text-white py-16 sm:py-20">
        <div className="container-mtr text-center max-w-2xl">
          <Building2 className="text-orange mx-auto mb-4" size={36} />
          <h2 className="font-display font-black text-[34px] sm:text-[44px] uppercase leading-tight">Close more jobs with accurate numbers</h2>
          <p className="text-white/80 text-[16px] mt-4">Join roofing contractors getting measurements in minutes. Your first report is just an address away.</p>
          <Link to={startHref} className="btn-primary !min-h-[52px] !px-8 text-[16px] mt-8 inline-flex" data-testid="footer-cta">
            Get started <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-fieldstone rounded-input p-3">
      <p className="text-[10px] uppercase tracking-[0.08em] text-mortar font-semibold">{label}</p>
      <p className="metric text-[20px] mt-0.5">{value}</p>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="card-mtr hover:shadow-modal transition-shadow duration-200">
      <div className="w-12 h-12 rounded-input bg-fieldstone grid place-items-center mb-4">
        <Icon className="text-orange" size={24} aria-hidden="true" />
      </div>
      <h3 className="font-bold text-navy text-[18px]">{title}</h3>
      <p className="text-[14px] text-mortar mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}
